import { useEffect, useRef, useState } from 'react'
import { Dialog } from 'radix-ui'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { FieldRenderer } from '../../components/FieldRenderer'
import { formatCurrencyPl, formatPlNumber } from '../../components/numberFormat'
import type { FieldDefinition } from '../../types'
import { useAnnex, useAnnexParametersSchema, useCancelAnnexDraft, usePatchAnnexDraft, usePublishAnnex } from '../hooks'
import type { PendingChange } from '../types'
import { AnnexableFieldsPicker, type PickedField } from './AnnexableFieldsPicker'
import { SimpleFieldInput } from './SimpleFieldInput'

const AUTOSAVE_MS = 800

/**
 * Reserved key inside {@code annex.extras} used to persist the draft's pending change
 * list alongside the rest of the draft metadata. Stripped before publish (the publish
 * payload carries the changes in its own structure) and unused for object_type='annex'
 * field_definition (those use real field_codes).
 */
const PENDING_CHANGES_KEY = '__pending_changes'

export function CreateAnnexSheet({
  contractId,
  contractVersion,
  contractExtras,
  annexId,
  onClose,
  onPublished,
}: {
  contractId: number
  contractVersion: number
  contractExtras: Record<string, unknown>
  annexId: number
  onClose: () => void
  onPublished?: (publishedAnnexId: number) => void
}) {
  const confirm = useConfirm()
  const annexQuery = useAnnex(contractId, annexId)
  const paramsSchemaQ = useAnnexParametersSchema(contractId)
  const patchMutation = usePatchAnnexDraft(contractId)
  const publishMutation = usePublishAnnex(contractId)
  const cancelMutation = useCancelAnnexDraft(contractId)

  const annex = annexQuery.data
  const [reason, setReason] = useState('')
  const [paramValues, setParamValues] = useState<Record<string, unknown>>({})
  const [changes, setChanges] = useState<PendingChange[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastSavedReason = useRef<string>('')
  const lastSavedParams = useRef<string>('{}')
  const lastSavedChanges = useRef<string>('[]')
  const hydratedFromVersion = useRef<number>(-1)

  // Sync state from server when the annex (or its version) changes - first load and
  // after every successful save. We hydrate the pending-changes list from extras.
  useEffect(() => {
    if (!annex) return
    if (hydratedFromVersion.current === annex.version) return
    hydratedFromVersion.current = annex.version
    setReason(annex.reason)
    lastSavedReason.current = annex.reason
    const persisted = readPendingChanges(annex.extras)
    setChanges(persisted)
    lastSavedChanges.current = JSON.stringify(persisted)
    const params = readParamValues(annex.extras)
    setParamValues(params)
    lastSavedParams.current = JSON.stringify(params)
  }, [annex?.id, annex?.version])

  // Debounced autosave: any change to reason, params, or pending-changes flushes through PATCH.
  useEffect(() => {
    if (!annex) return
    const serializedChanges = JSON.stringify(changes)
    const serializedParams = JSON.stringify(paramValues)
    const reasonDirty = reason !== lastSavedReason.current
    const changesDirty = serializedChanges !== lastSavedChanges.current
    const paramsDirty = serializedParams !== lastSavedParams.current
    if (!reasonDirty && !changesDirty && !paramsDirty) return

    const t = setTimeout(() => {
      const mergedExtras = buildExtras(annex.extras, paramValues, changes)
      patchMutation.mutate(
        {
          annexId: annex.id,
          payload: {
            reason,
            attachmentFileId: annex.attachmentFileId,
            extras: mergedExtras,
            expectedVersion: annex.version,
          },
        },
        {
          onSuccess: () => {
            lastSavedReason.current = reason
            lastSavedChanges.current = serializedChanges
            lastSavedParams.current = serializedParams
          },
          onError: e => setError(formatError(e)),
        },
      )
    }, AUTOSAVE_MS)
    return () => clearTimeout(t)
  }, [reason, paramValues, changes, annex?.id, annex?.version])

  const addChange = (picked: PickedField) => {
    const oldValue = contractExtras[picked.field.fieldCode] ?? null
    setChanges(prev => [
      ...prev.filter(c => c.fieldCode !== picked.field.fieldCode),
      {
        sectionCode: picked.section.code,
        sectionLabel: picked.section.labelPl,
        fieldCode: picked.field.fieldCode,
        fieldLabel: picked.field.labelPl,
        fieldType: picked.field.fieldType,
        oldValue,
        newValue: oldValue,
      },
    ])
  }

  const updateChangeValue = (fieldCode: string, newValue: unknown) => {
    setChanges(prev => prev.map(c => (c.fieldCode === fieldCode ? { ...c, newValue } : c)))
  }

  const removeChange = (fieldCode: string) => {
    setChanges(prev => prev.filter(c => c.fieldCode !== fieldCode))
  }

  const handlePublish = () => {
    if (!annex) return
    setError(null)
    if (reason.trim().length === 0) {
      setError('Powód aneksu jest wymagany.')
      return
    }
    if (changes.length === 0) {
      setError('Dodaj co najmniej jedno pole do zmiany.')
      return
    }
    publishMutation.mutate(
      {
        annexId: annex.id,
        payload: {
          changes: changes.map(c => ({
            sectionCode: c.sectionCode,
            fieldCode: c.fieldCode,
            newValue: c.newValue,
          })),
          expectedAnnexVersion: annex.version,
          expectedContractVersion: contractVersion,
        },
      },
      {
        onSuccess: a => {
          onPublished?.(a.id)
          onClose()
        },
        onError: e => setError(formatError(e)),
      },
    )
  }

  const handleCancel = async () => {
    if (!annex) return
    const ok = await confirm({
      title: 'Anulować draft aneksu?',
      description: 'Lista zmian zostanie utracona. Operacja jest nieodwracalna.',
      confirmLabel: 'Anuluj draft',
      variant: 'destructive',
    })
    if (!ok) return
    cancelMutation.mutate(
      { annexId: annex.id, expectedVersion: annex.version },
      { onSuccess: onClose, onError: e => setError(formatError(e)) },
    )
  }

  const disabledFieldCodes = new Set(changes.map(c => c.fieldCode))
  const isBusy = patchMutation.isPending || publishMutation.isPending || cancelMutation.isPending

  return (
    <>
      <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
          <Dialog.Content className="fixed right-0 top-0 z-40 h-full w-[560px] bg-white shadow-xl p-5 overflow-auto flex flex-col">
            <Dialog.Title className="text-base font-semibold">Aneks do umowy</Dialog.Title>
            <Dialog.Description className="text-xs text-slate-500 mt-1">
              Wprowadź powód aneksu i wybierz pola, które się zmieniają. Zmiany zostaną
              utrwalone w umowie i historii dopiero po opublikowaniu.
            </Dialog.Description>

            {annexQuery.isLoading && <p className="mt-4 text-sm text-slate-500">Ładowanie...</p>}
            {annexQuery.error && (
              <p className="mt-4 text-sm text-red-600">Nie udało się załadować aneksu: {String(annexQuery.error)}</p>
            )}

            {annex && (
              <div className="mt-4 flex-1 flex flex-col gap-5 text-sm">
                <Section title="Parametry aneksu">
                  <div className="flex flex-col gap-4">
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] uppercase tracking-wide text-slate-600">Powód aneksu</span>
                      <textarea
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        disabled={isBusy}
                        className="border rounded px-2 py-1.5 min-h-[5rem]"
                        placeholder="np. klient dobiera 6 paneli (rozbudowa)"
                      />
                    </label>
                    {paramsSchemaQ.data?.fields.map(fd => (
                      <FieldRenderer
                        key={fd.fieldCode}
                        field={fd as FieldDefinition}
                        value={paramValues[fd.fieldCode]}
                        mode="EDIT"
                        onChange={v => setParamValues(prev => ({ ...prev, [fd.fieldCode]: v }))}
                        disabled={isBusy}
                      />
                    ))}
                    {patchMutation.isPending && (
                      <span className="text-xs text-slate-400">Zapisuję...</span>
                    )}
                  </div>
                </Section>

                <Section
                  title="Zmiany w umowie"
                  action={
                    <button
                      type="button"
                      onClick={() => setPickerOpen(true)}
                      disabled={isBusy}
                      className="px-2 py-1 text-xs border rounded hover:bg-slate-50"
                    >
                      + Dodaj pole
                    </button>
                  }
                >
                  {changes.length === 0 ? (
                    <p className="text-xs text-slate-500">Brak pól. Kliknij "Dodaj pole", aby wybrać.</p>
                  ) : (
                    <ul className="flex flex-col gap-3">
                      {changes.map(c => (
                        <li key={c.fieldCode} className="border rounded p-3">
                          <div className="flex justify-between items-start gap-2">
                            <div>
                              <div className="text-sm font-medium">{c.fieldLabel}</div>
                              <div className="text-xs text-slate-500">
                                {c.sectionLabel} · {c.fieldType}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeChange(c.fieldCode)}
                              disabled={isBusy}
                              className="text-xs text-slate-500 hover:text-red-600"
                            >
                              Usuń
                            </button>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                                Dotychczas
                              </div>
                              <div className="text-sm text-slate-700 px-2 py-1.5 bg-slate-50 rounded border border-dashed">
                                {formatDisplay(c.fieldType, c.oldValue)}
                              </div>
                            </div>
                            <div>
                              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                                Po aneksie
                              </div>
                              <SimpleFieldInput
                                fieldType={c.fieldType}
                                value={c.newValue}
                                onChange={v => updateChangeValue(c.fieldCode, v)}
                                disabled={isBusy}
                              />
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <div className="mt-auto flex justify-between items-center pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isBusy}
                    className="px-3 py-1.5 text-sm border rounded text-red-700 hover:bg-red-50"
                  >
                    Anuluj draft
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isBusy}
                      className="px-3 py-1.5 text-sm border rounded"
                    >
                      Zapisz draft
                    </button>
                    <button
                      type="button"
                      onClick={handlePublish}
                      disabled={isBusy}
                      className="px-3 py-1.5 text-sm bg-sky-600 text-white rounded hover:bg-sky-700 disabled:opacity-50"
                    >
                      {publishMutation.isPending ? 'Publikuję...' : 'Opublikuj'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <AnnexableFieldsPicker
        contractId={contractId}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={addChange}
        disabledFieldCodes={disabledFieldCodes}
      />
    </>
  )
}

function readPendingChanges(extras: Record<string, unknown>): PendingChange[] {
  const raw = extras[PENDING_CHANGES_KEY]
  if (!Array.isArray(raw)) return []
  return raw.filter(isPendingChange)
}

/** Everything in extras except the reserved pending-changes key. */
function readParamValues(extras: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(extras)) {
    if (k === PENDING_CHANGES_KEY) continue
    out[k] = v
  }
  return out
}

/** Reconstruct extras = paramValues ∪ {__pending_changes: changes if non-empty}. */
function buildExtras(
  _previousExtras: Record<string, unknown>,
  paramValues: Record<string, unknown>,
  changes: PendingChange[],
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...paramValues }
  if (changes.length > 0) next[PENDING_CHANGES_KEY] = changes
  return next
}

function isPendingChange(v: unknown): v is PendingChange {
  if (!v || typeof v !== 'object') return false
  const c = v as Record<string, unknown>
  return (
    typeof c.sectionCode === 'string' &&
    typeof c.fieldCode === 'string' &&
    typeof c.fieldType === 'string' &&
    typeof c.sectionLabel === 'string' &&
    typeof c.fieldLabel === 'string'
  )
}


function Section({
  title,
  action,
  children,
}: {
  title: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-wide text-slate-600 font-semibold">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  )
}

function formatDisplay(fieldType: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Tak' : 'Nie'
  if (fieldType === 'CURRENCY' && typeof v === 'number') return formatCurrencyPl(v)
  if (fieldType === 'NUMBER' && typeof v === 'number') return formatPlNumber(v)
  if (Array.isArray(v)) return v.join(', ')
  return String(v)
}

function formatError(e: unknown): string {
  if (!e || typeof e !== 'object') return 'Błąd serwera'
  const anyErr = e as { response?: { data?: { detail?: string; title?: string } }; message?: string }
  return (
    anyErr.response?.data?.detail ??
    anyErr.response?.data?.title ??
    anyErr.message ??
    'Błąd serwera'
  )
}
